import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Rnd } from 'react-rnd';
import { ElementFrame, FrameElementId, PitchSlide, SlideLayout } from '../../data/slides';
import {
  getLayerFrame,
  isBuiltinLayerId,
  LayerId,
  resolveLayerOrder,
  zIndexForLayer,
} from '../../data/layerOrder';
import { getComponentById, isComponentId } from '../../data/slideComponents';
import { appTheme, pitchTheme } from '../../styles/theme';
import SlideLogo from '../SlideLogo';
import SlideComponentView from '../SlideComponentView';
import TapePhoto from '../TapePhoto';
import {
  CardHeading,
  CardHighlightItem,
  CardHighlightList,
  SlideBackgroundLayer,
} from '../PitchCard';

const CanvasFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 920 / 520;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusLg};
  overflow: hidden;
  background: ${pitchTheme.cream};
  box-shadow: ${appTheme.shadowLg};
`;

const BackgroundHitArea = styled.button<{ $selected: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  outline: ${({ $selected }) =>
    $selected ? `2px solid ${appTheme.green}` : '1px dashed rgba(19, 78, 68, 0.2)'};
  outline-offset: -2px;

  &:hover {
    outline: 2px solid ${appTheme.green};
  }
`;

const ElementContent = styled.div<{ $selected: boolean }>`
  width: 100%;
  height: 100%;
  overflow: hidden;
  cursor: move;
  user-select: none;
  touch-action: none;
  outline: ${({ $selected }) =>
    $selected ? `2px solid ${appTheme.green}` : '1px dashed rgba(19, 78, 68, 0.35)'};
  outline-offset: -1px;
  position: relative;
  z-index: 1;

  &:hover {
    outline: 2px solid ${appTheme.green};
  }
`;

const CenteredContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CanvasDraggableLayerProps {
  layerId: LayerId;
  rect: PixelRect;
  zIndex: number;
  isSelected: boolean;
  onSelectLayer: (layerId: LayerId) => void;
  onInteractionStart: (layerId: LayerId) => void;
  onInteractionEnd: () => void;
  onLayerFrameChange: (layerId: LayerId, frame: ElementFrame) => void;
  toPercent: (x: number, y: number, width: number, height: number) => ElementFrame;
  children: React.ReactNode;
}

/** Uncontrolled Rnd layer — position is managed internally during drag for smooth movement. */
const CanvasDraggableLayer: React.FC<CanvasDraggableLayerProps> = ({
  layerId,
  rect,
  zIndex,
  isSelected,
  onSelectLayer,
  onInteractionStart,
  onInteractionEnd,
  onLayerFrameChange,
  toPercent,
  children,
}) => (
  <Rnd
    bounds="parent"
    default={{
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    }}
    style={{ zIndex }}
    className="canvas-layer"
    enableUserSelectHack={false}
    onDragStart={() => {
      onInteractionStart(layerId);
      onSelectLayer(layerId);
    }}
    onDragStop={(_event, data) => {
      onInteractionEnd();
      onLayerFrameChange(
        layerId,
        toPercent(data.x, data.y, data.node.offsetWidth, data.node.offsetHeight),
      );
    }}
    onResizeStart={() => {
      onInteractionStart(layerId);
      onSelectLayer(layerId);
    }}
    onResizeStop={(_event, _direction, element, _delta, position) => {
      onInteractionEnd();
      onLayerFrameChange(
        layerId,
        toPercent(position.x, position.y, element.offsetWidth, element.offsetHeight),
      );
    }}
  >
    <ElementContent
      $selected={isSelected}
      onPointerDown={(event) => {
        event.stopPropagation();
        onSelectLayer(layerId);
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      {children}
    </ElementContent>
  </Rnd>
);

interface SlideCanvasProps {
  slide: PitchSlide;
  layout: SlideLayout;
  logoUrl?: string | null;
  selectedLayerId: LayerId | null;
  onSelectLayer: (layerId: LayerId | null) => void;
  onLayerFrameChange: (layerId: LayerId, frame: ElementFrame) => void;
}

const renderBuiltinContent = (
  elementId: FrameElementId,
  slide: PitchSlide,
  layout: SlideLayout,
  logoUrl?: string | null,
): React.ReactNode => {
  switch (elementId) {
    case 'logo':
      return <SlideLogo logoUrl={logoUrl} />;
    case 'title':
      return <CardHeading>{slide.title}</CardHeading>;
    case 'highlights':
      return (
        <CardHighlightList>
          {slide.highlights.map((point, index) => (
            <CardHighlightItem key={`${index}-${point}`}>{point}</CardHighlightItem>
          ))}
        </CardHighlightList>
      );
    case 'image':
      return (
        <CenteredContent>
          <TapePhoto
            src={slide.imageUrl}
            alt={slide.imageAlt}
            borderRadius={layout.imageStyle?.borderRadius}
            fillFrame
            showTape={false}
          />
        </CenteredContent>
      );
  }
};

const frameLayoutKey = (
  layerId: LayerId,
  frame: ElementFrame,
  canvasWidth: number,
  canvasHeight: number,
): string =>
  `${layerId}:${canvasWidth}x${canvasHeight}:${frame.x},${frame.y},${frame.w},${frame.h}`;

const SlideCanvas: React.FC<SlideCanvasProps> = ({
  slide,
  layout,
  logoUrl,
  selectedLayerId,
  onSelectLayer,
  onLayerFrameChange,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [activeLayerId, setActiveLayerId] = useState<LayerId | null>(null);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const measure = () => {
      setCanvasSize({ width: canvas.offsetWidth, height: canvas.offsetHeight });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  const toPixels = useCallback(
    (frame: ElementFrame): PixelRect => ({
      x: (frame.x / 100) * canvasSize.width,
      y: (frame.y / 100) * canvasSize.height,
      width: (frame.w / 100) * canvasSize.width,
      height: (frame.h / 100) * canvasSize.height,
    }),
    [canvasSize]
  );

  const toPercent = useCallback(
    (x: number, y: number, width: number, height: number): ElementFrame => ({
      x: (x / canvasSize.width) * 100,
      y: (y / canvasSize.height) * 100,
      w: (width / canvasSize.width) * 100,
      h: (height / canvasSize.height) * 100,
    }),
    [canvasSize]
  );

  const isMeasured = canvasSize.width > 0 && canvasSize.height > 0;
  const layerOrder = resolveLayerOrder(layout);

  const renderLayerContent = (layerId: LayerId): React.ReactNode => {
    if (isComponentId(layerId)) {
      const component = getComponentById(layout, layerId);
      return component ? <SlideComponentView component={component} /> : null;
    }
    if (isBuiltinLayerId(layerId)) {
      return renderBuiltinContent(layerId, slide, layout, logoUrl);
    }
    return null;
  };

  return (
    <CanvasFrame
      ref={canvasRef}
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest('.canvas-layer')) {
          return;
        }
        onSelectLayer(null);
      }}
    >
      <SlideBackgroundLayer background={layout.background} />
      <BackgroundHitArea
        type="button"
        aria-label={t('editor.canvas.selectBackground')}
        $selected={selectedLayerId === 'background'}
        onClick={(event) => {
          event.stopPropagation();
          onSelectLayer('background');
        }}
      />
      {isMeasured &&
        layerOrder.map((layerId) => {
          const frame = getLayerFrame(layout, layerId);
          if (!frame) {
            return null;
          }
          const rect = toPixels(frame);
          const layerIndex = layerOrder.indexOf(layerId);
          const isSelected = selectedLayerId === layerId;
          const isActive = activeLayerId === layerId;

          return (
            <CanvasDraggableLayer
              key={frameLayoutKey(layerId, frame, canvasSize.width, canvasSize.height)}
              layerId={layerId}
              rect={rect}
              zIndex={
                isActive ? 2000 : zIndexForLayer(layerIndex, isSelected ? 1000 : 0)
              }
              isSelected={isSelected}
              onSelectLayer={(id) => onSelectLayer(id)}
              onInteractionStart={setActiveLayerId}
              onInteractionEnd={() => setActiveLayerId(null)}
              onLayerFrameChange={onLayerFrameChange}
              toPercent={toPercent}
            >
              {renderLayerContent(layerId)}
            </CanvasDraggableLayer>
          );
        })}
    </CanvasFrame>
  );
};

export default SlideCanvas;
